import { toWebsiteLang } from './langCodeMap';

/** Host for the whitelabels.com public marketing site (the legal + data-request pages). */
const SITE_BASE = 'https://whitelabels.com';

/**
 * Build a whitelabels.com public-site URL for the active app language, locale-aware.
 *
 * English is the international default (served at the site root, no locale segment); every other
 * language uses its public-site locale as a leading path segment. That segment uses the
 * whitelabels.com public-site scheme, NOT the app's i18n folder code, and the two diverge for the
 * Intl-safe codes (`bra` -> `/br`, `arg` -> `/ar`) and South Africa (`za`); `toWebsiteLang` is the
 * single source of truth for that mapping. Locales the public site has no localised page for (e.g.
 * `ve`, `za`, `arg`) resolve to the English page via the site's own 301 redirect, so the segment is
 * always safe to emit and the app never has to track which locales are published.
 *
 * `path` is the site's CANONICAL trailing-slash permalink (`privacy-policy/`, `delete-request/`);
 * the site 301-redirects the legacy `.html` forms to these, so callers link straight to canonical.
 *
 * @param language active i18next language (e.g. `mx`, `bra`, `en-US`); region form is normalised
 *                 down to the short code and the case is folded.
 * @param path site-relative permalink WITHOUT a leading slash and WITH its trailing slash.
 */
export const websiteUrl = (language: string, path: string): string => {
  const code = toWebsiteLang((language || '').split('-')[0].toLowerCase());
  const segment = code && code !== 'en' ? `/${code}` : '';
  return `${SITE_BASE}${segment}/${path}`;
};

/** The public legal pages linked across the app. */
export type LegalPage = 'privacy-policy' | 'terms-of-use';

/** whitelabels.com legal-page URL for the active language (canonical `…/<page>/`). */
export const legalUrl = (language: string, page: LegalPage): string => websiteUrl(language, `${page}/`);

/**
 * whitelabels.com data-deletion request URL for the active language: the public "request the
 * erasure of my data" page (GDPR right-to-erasure / CCPA right-to-delete) linked from Settings.
 */
export const dataDeletionUrl = (language: string): string => websiteUrl(language, 'delete-request/');
